export interface BatchSectionOrderedItem {
  id: string
  sectionId?: string
}

/** Resolves the live section for a source without replacing a missing target tombstone. */
export function findBatchSectionBySourceAffinity<T>(
  sections: Readonly<Record<string, T>>,
  sourceSectionAffinityByImageId: Readonly<Record<string, string>>,
  sourceId: string,
): T | undefined {
  const sectionId = sourceSectionAffinityByImageId[sourceId]
  return sectionId ? sections[sectionId] : undefined
}

/** Returns section members in their existing global display order. */
export function getBatchSectionMemberIds(
  items: readonly BatchSectionOrderedItem[],
  sectionId: string,
): string[] {
  return items.filter((item) => item.sectionId === sectionId).map((item) => item.id)
}

/** Appends after the last current member, or at the end when the section is empty. */
export function getBatchSectionInsertionIndex(
  items: readonly BatchSectionOrderedItem[],
  sectionId: string,
): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (items[index].sectionId === sectionId) return index + 1
  }
  return items.length
}

/**
 * Rebuilds global order so each section's members form one contiguous run.
 * Encounter order of the first member of each section (and of loose images)
 * is preserved — matching `selectBatchLayoutBlocks` coalescing so display and
 * export stay aligned after membership changes.
 */
export function compactBatchSectionMembership<T extends BatchSectionOrderedItem>(
  items: readonly T[],
): T[] {
  const membersBySection = new Map<string, T[]>()
  for (const item of items) {
    if (!item.sectionId) continue
    const members = membersBySection.get(item.sectionId) ?? []
    members.push(item)
    membersBySection.set(item.sectionId, members)
  }

  const result: T[] = []
  const emittedSections = new Set<string>()
  for (const item of items) {
    if (!item.sectionId) {
      result.push(item)
      continue
    }
    if (emittedSections.has(item.sectionId)) continue
    result.push(...(membersBySection.get(item.sectionId) ?? []))
    emittedSections.add(item.sectionId)
  }
  return result
}

/** Reorders a stable subset while preserving each item's membership. */
export function reorderBatchItems<T extends BatchSectionOrderedItem>(
  items: readonly T[],
  itemIds: readonly string[],
  beforeId: string | null,
): { items: T[]; changed: boolean } {
  const movingIds = new Set(itemIds)
  const moving = items.filter((item) => movingIds.has(item.id))
  if (!moving.length) return { items: [...items], changed: false }

  const rest = items.filter((item) => !movingIds.has(item.id))
  const beforeIndex = beforeId ? rest.findIndex((item) => item.id === beforeId) : -1
  const insertionIndex = beforeIndex === -1 ? rest.length : beforeIndex
  const next = [...rest.slice(0, insertionIndex), ...moving, ...rest.slice(insertionIndex)]
  const changed = next.some((item, index) => items[index]?.id !== item.id)
  return { items: next, changed }
}

/**
 * Computes one atomic image move without mutating the input collection.
 * The same operation powers the UI no-op guard and the reducer commit so the
 * preview cannot drift from the state transition that is recorded in history.
 */
export function moveBatchSectionItems<T extends BatchSectionOrderedItem>(
  items: readonly T[],
  itemIds: readonly string[],
  beforeId: string | null,
  destinationSectionId: string | undefined,
): { items: T[]; changed: boolean } {
  const movingIds = new Set(itemIds)
  const originalSectionById = new Map(items.map((item) => [item.id, item.sectionId]))
  const reordered = reorderBatchItems(items, itemIds, beforeId)
  const next = reordered.items.map((item) => movingIds.has(item.id)
    ? { ...item, sectionId: destinationSectionId }
    : item)
  const compacted = compactBatchSectionMembership(next)
  const changed = compacted.some((item, index) => (
    items[index]?.id !== item.id || originalSectionById.get(item.id) !== item.sectionId
  ))

  return { items: compacted, changed }
}
